import { Deferred, router, usePage } from "@inertiajs/react";
import { useHover } from "@uidotdev/usehooks";
import { format, isSameHour, isToday, parse } from "date-fns";
import {
	CalendarIcon,
	Check,
	ChevronLeft,
	ChevronRight,
	ChevronsLeft,
	ChevronsRight,
	ChevronsUpDown,
	EllipsisIcon,
	Hash,
	Search,
} from "lucide-react";
import {
	type ComponentProps,
	Fragment,
	Suspense,
	useCallback,
	useMemo,
	useState,
} from "react";
import { useTranslation } from "react-i18next";
import { useDateContext } from "@/components/providers/date-provider";
import { LoadingBasic } from "@/components/shared/loading";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import {
	Command,
	CommandEmpty,
	CommandGroup,
	CommandInput,
	CommandItem,
	CommandList,
	CommandSeparator,
} from "@/components/ui/command";
import {
	HoverCard,
	HoverCardContent,
	HoverCardTrigger,
} from "@/components/ui/hover-card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
	Popover,
	PopoverContent,
	PopoverTrigger,
} from "@/components/ui/popover";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectSeparator,
	SelectTrigger,
	SelectValue,
} from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import {
	Sheet,
	SheetClose,
	SheetContent,
	SheetFooter,
	SheetHeader,
	SheetTitle,
	SheetTrigger,
} from "@/components/ui/sheet";
import { Skeleton } from "@/components/ui/skeleton";
import {
	checkPastTimeSlot,
	checkUnder2Hours,
	formatTimeLabel,
	getTherapistAvailabilityForDate,
	INTERVAL_MINUTES,
	SLOT_HEIGHT,
	START_HOUR,
} from "@/hooks/use-calendar-schedule";
import { deepTransformKeysToSnakeCase } from "@/hooks/use-change-case";
import { useIsMobile } from "@/hooks/use-mobile";
import { appointmentBlockClass } from "@/lib/appointments/status-styles";
import { groupLocationsByCountry } from "@/lib/locations";
import {
	cn,
	debounce,
	generateInitials,
	populateQueryParams,
} from "@/lib/utils";
import type { DaySchedulesPageGlobalProps } from "@/pages/AdminPortal/Therapist/DaySchedules";
import type { Appointment } from "@/types/admin-portal/appointment";
import type { Therapist } from "@/types/admin-portal/therapist";
import type { GlobalPageProps } from "@/types/globals";
import type { Metadata } from "@/types/pagy";
import PatientDetailsSection, {
	AppointmentDetailsSection,
	PICDetailsSection,
	TherapistDetailsSection,
} from "./appointment-details";

type GeneralProps = {
	selectedDate: Date;
	therapists: Therapist[];
	timeSlots: string[];
};

// * for calendar container
export interface CalendarContainerProps extends ComponentProps<"div"> {}

export function CalendarContainer({
	className,
	children,
}: CalendarContainerProps) {
	return (
		<div
			className={cn(
				"w-full bg-background border rounded-lg border-border",
				className,
			)}
		>
			{children}
		</div>
	);
}

// * for the date toolbar component
export interface DateToolbarProps
	extends ComponentProps<"div">,
		Pick<GeneralProps, "selectedDate"> {
	actions: {
		goToToday: () => void;
		goToPrevDay: () => void;
		goToNextDay: () => void;
		goToByDate: (value: Date) => void;
	};
}

export function ScheduleDateToolbar({
	className,
	selectedDate,
	actions,
}: DateToolbarProps) {
	const isMobile = useIsMobile();
	const { locale, tzDate } = useDateContext();
	const { t } = useTranslation("therapist-schedules");
	const displayedDate = useMemo(
		() => format(selectedDate, "PPPP", { locale, in: tzDate }),
		[selectedDate, locale, tzDate],
	);

	return (
		<div
			className={cn(
				"flex flex-col gap-4 md:flex-row md:items-center",
				className,
			)}
		>
			<div className="flex flex-col items-center gap-2 md:order-last md:flex-row">
				<Button
					variant={isToday(selectedDate) ? "default" : "primary-outline"}
					className={cn(isMobile && "w-full")}
					onClick={actions.goToToday}
				>
					{t("date_toolbar.today_btn")}
				</Button>

				<Popover>
					<PopoverTrigger asChild>
						<Button
							variant="primary-outline"
							size="icon"
							className={cn("md:order-first", isMobile && "w-full")}
						>
							<CalendarIcon className="size-4" />
							{selectedDate && isMobile && displayedDate}
						</Button>
					</PopoverTrigger>
					<PopoverContent className="w-auto p-0" align="center" side="bottom">
						<Calendar
							initialFocus
							mode="single"
							selected={selectedDate}
							onSelect={(value) => {
								if (!value) return;

								actions.goToByDate(value);
							}}
						/>
					</PopoverContent>
				</Popover>
			</div>

			<div className="flex items-center w-full gap-4 md:gap-0">
				<Button
					variant="ghost"
					size="sm"
					className="p-0 size-8 focus:border focus:border-border"
					onClick={actions.goToPrevDay}
				>
					<ChevronLeft />
				</Button>

				<p className="flex-1 md:flex-initial md:w-auto font-semibold text-center md:order-first lg:mr-2 text-nowrap">
					{displayedDate}
				</p>

				<Button
					variant="ghost"
					size="sm"
					className="p-0 size-8 focus:border focus:border-border"
					onClick={actions.goToNextDay}
				>
					<ChevronRight />
				</Button>
			</div>
		</div>
	);
}

// * for the pagination component
export interface SchedulePaginationProps extends ComponentProps<"div"> {
	metadata: Metadata;
	actions: {
		goToFirstPage: () => void;
		goToPrevPage: () => void;
		goToNextPage: () => void;
		goToLastPage: () => void;
		goToPage: (page: number) => void;
	};
}

export function SchedulePagination({
	className,
	metadata,
	actions,
}: SchedulePaginationProps) {
	const { t } = useTranslation("therapist-schedules");
	const { t: tp } = useTranslation("translation", {
		keyPrefix: "components.pagination",
	});
	const isPrevDisabled = useMemo(() => !metadata.prev, [metadata.prev]);
	const isNextDisabled = useMemo(() => !metadata.next, [metadata.next]);
	const isSinglePage = useMemo(() => metadata.pages <= 1, [metadata.pages]);
	const minPage = 1;
	const maxPage = useMemo(
		() => metadata.last || metadata.pages || metadata.page || 1,
		[metadata.last, metadata.pages, metadata.page],
	);
	const { goToFirstPage, goToPrevPage, goToNextPage, goToLastPage, goToPage } =
		actions;

	const handleGoToPage = useCallback(
		(value: string) => {
			if (!value) return;
			const parsedValue = Number(value);
			if (Number.isNaN(parsedValue)) return;
			const clampedValue = Math.min(Math.max(parsedValue, minPage), maxPage);
			goToPage(clampedValue);
		},
		[goToPage, maxPage],
	);

	return (
		<div
			className={cn(
				"flex items-center flex-col md:flex-row justify-between w-full bg-background p-2 border border-border gap-4",
				className,
			)}
		>
			<div className="flex items-center gap-3 justify-between w-full">
				<span className="text-sm text-muted-foreground text-nowrap">
					{t("pagination.showing")} {metadata.from}–{metadata.to} {tp("of")}{" "}
					{metadata.count} {t("pagination.therapists")}
				</span>

				<span className="text-sm text-muted-foreground md:hidden text-nowrap">
					{tp("page")} {metadata.page} {tp("of")} {metadata.pages}
				</span>
			</div>

			<div className="flex items-center gap-3">
				<span className="hidden text-sm text-muted-foreground md:inline-flex text-nowrap">
					{tp("page")} {metadata.page} {tp("of")} {metadata.pages}
				</span>

				<div className="flex gap-2">
					<Button
						variant="outline"
						className="p-0 size-8 bg-card"
						disabled={isPrevDisabled}
						onClick={goToFirstPage}
					>
						<span className="sr-only">{tp("go_to_first")}</span>
						<ChevronsLeft className="size-4" />
					</Button>
					<Button
						variant="outline"
						className="p-0 size-8 bg-card"
						disabled={isPrevDisabled}
						onClick={goToPrevPage}
					>
						<span className="sr-only">{tp("go_to_previous")}</span>
						<ChevronLeft className="size-4" />
					</Button>

					<Popover>
						<PopoverTrigger asChild disabled={isSinglePage}>
							<Button
								variant="outline"
								size="icon"
								className="p-0 size-8 bg-card"
							>
								{isSinglePage ? (
									<span className="text-xs font-semibold">1</span>
								) : (
									<EllipsisIcon className="size-4" />
								)}
							</Button>
						</PopoverTrigger>
						<PopoverContent className="w-full" align="center">
							<div className="grid gap-4">
								<div className="flex items-center gap-4">
									<Label htmlFor="goto-page" className="text-nowrap">
										{tp("go_to_page")}
									</Label>
									<Input
										id="goto-page"
										type="number"
										defaultValue={metadata.page}
										min={minPage}
										max={maxPage}
										className="h-8"
										onChange={(event) => handleGoToPage(event.target.value)}
									/>
								</div>
							</div>
						</PopoverContent>
					</Popover>

					<Button
						variant="outline"
						className="p-0 size-8 bg-card"
						disabled={isNextDisabled}
						onClick={goToNextPage}
					>
						<span className="sr-only">{tp("go_to_next")}</span>
						<ChevronRight className="size-4" />
					</Button>
					<Button
						variant="outline"
						className="p-0 size-8 bg-card"
						disabled={isNextDisabled}
						onClick={goToLastPage}
					>
						<span className="sr-only">{tp("go_to_last")}</span>
						<ChevronsRight className="size-4" />
					</Button>
				</div>
			</div>
		</div>
	);
}

// * for schedule filter data
export function ScheduleFilters() {
	const { props: globalProps, url: pageURL } =
		usePage<DaySchedulesPageGlobalProps>();
	const { t } = useTranslation("therapist-schedules");
	const empTypes = useMemo(
		() => globalProps.filterOptions?.employmentTypes || [],
		[globalProps.filterOptions?.employmentTypes],
	);
	const locations = useMemo(
		() => globalProps?.filterOptions?.locations,
		[globalProps?.filterOptions?.locations],
	);
	const groupedLocations = useMemo(
		() => groupLocationsByCountry(globalProps?.filterOptions?.locations || []),
		[globalProps?.filterOptions?.locations],
	);
	const [filterBy, setFilterBy] = useState({
		search: globalProps?.adminPortal?.currentQuery?.search || "",
		employmentType:
			globalProps?.adminPortal?.currentQuery?.employmentType || "",
		city: globalProps?.adminPortal?.currentQuery?.city || "",
	});
	const updateQueryParams = useCallback(
		debounce((url) => {
			router.get(
				url,
				{},
				{ preserveState: true, only: ["therapists", "params", "adminPortal"] },
			);
		}, 500),
		[],
	);
	const handleFilterBy = useCallback(
		({ value, type }: { value: string; type: keyof typeof filterBy }) => {
			const nextFilters = { ...filterBy, [type]: value };
			setFilterBy(nextFilters);

			const { fullUrl } = populateQueryParams(
				pageURL,
				deepTransformKeysToSnakeCase({
					...nextFilters,
					// null value means reset the another param value
					page: null,
				}),
			);
			updateQueryParams(fullUrl);
		},
		[pageURL, filterBy, updateQueryParams],
	);

	return (
		<div className="grid w-full grid-cols-1 gap-4 md:grid-cols-4 lg:grid-cols-5 md:gap-2">
			<div className="col-span-full md:col-span-2">
				<Input
					value={filterBy.search}
					StartIcon={{ icon: Search }}
					type="text"
					placeholder={t("filters.search_placeholder")}
					onChange={(event) => {
						event.preventDefault();
						handleFilterBy({ type: "search", value: event.target.value });
					}}
				/>
			</div>

			<Deferred
				data={["filterOptions"]}
				fallback={
					<Skeleton className="w-full rounded-md col-span-full md:col-span-1 h-9" />
				}
			>
				<div className="col-span-full md:col-span-1">
					<Select
						value={filterBy.employmentType || ""}
						onValueChange={(value) => {
							handleFilterBy({ type: "employmentType", value });
						}}
					>
						<SelectTrigger
							className={cn(
								"",
								!filterBy.employmentType ? "text-muted-foreground" : "",
							)}
						>
							<SelectValue placeholder={t("filters.employment_type")} />
						</SelectTrigger>

						<SelectContent>
							{empTypes?.length ? (
								<>
									{empTypes.map((type) => (
										<SelectItem key={type} value={type}>
											<span>{type}</span>
										</SelectItem>
									))}
									{filterBy.employmentType && (
										<>
											<SelectSeparator />
											<Button
												className="w-full px-2 font-medium uppercase"
												variant="ghost"
												size="sm"
												onClick={(e) => {
													e.stopPropagation();
													handleFilterBy({ type: "employmentType", value: "" });
												}}
											>
												Clear
											</Button>
										</>
									)}
								</>
							) : (
								<SelectItem value="no items" disabled>
									No employment type found.
								</SelectItem>
							)}
						</SelectContent>
					</Select>
				</div>
			</Deferred>

			<Deferred
				data={["filterOptions"]}
				fallback={
					<Skeleton className="w-full rounded-md col-span-full md:col-span-1 h-9" />
				}
			>
				<div className="col-span-full md:col-span-1">
					<Popover>
						<PopoverTrigger asChild>
							<Button
								variant="outline"
								className={cn(
									"relative w-full flex justify-between font-normal",
									!filterBy?.city && "text-muted-foreground",
								)}
							>
								<p className="truncate">
									{filterBy?.city
										? locations?.find(
												(location) => location.city === filterBy?.city,
											)?.city
										: t("filters.region_trigger")}
								</p>
								<ChevronsUpDown className="opacity-50" />
							</Button>
						</PopoverTrigger>
						<PopoverContent
							className="p-0 w-[300px]"
							align="start"
							side="bottom"
						>
							<Command>
								<CommandInput
									placeholder={t("filters.region.search")}
									className="h-9"
									autoComplete="address-level2"
								/>
								<CommandList>
									<CommandEmpty>{t("filters.region.empty")}</CommandEmpty>
									{groupedLocations?.map((location) => (
										<Fragment key={location.country}>
											<span className="block px-2 py-2 text-xs font-bold text-primary-foreground bg-primary">
												{location.country}
											</span>

											{location.states.map((state, stateIndex) => (
												<CommandGroup key={state.name} heading={state.name}>
													{state.cities.map((city) => (
														<CommandItem
															key={city.name}
															value={city.name}
															onSelect={() =>
																handleFilterBy({
																	type: "city",
																	value: city.name,
																})
															}
														>
															<span>{city.name}</span>
															<Check
																className={cn(
																	"ml-auto",
																	city.name === filterBy?.city
																		? "opacity-100"
																		: "opacity-0",
																)}
															/>
														</CommandItem>
													))}
													{location.states.length !== stateIndex + 1 && (
														<CommandSeparator className="mt-2" />
													)}
												</CommandGroup>
											))}
										</Fragment>
									))}
								</CommandList>

								<CommandList>
									<CommandGroup>
										<CommandSeparator className="mb-2" />
										<CommandItem
											onSelect={() => {
												handleFilterBy({ type: "city", value: "" });
											}}
										>
											<span className="mx-auto font-medium text-center uppercase w-fit">
												Clear
											</span>
										</CommandItem>
									</CommandGroup>
								</CommandList>
							</Command>
						</PopoverContent>
					</Popover>
				</div>
			</Deferred>
		</div>
	);
}

// * for calendar header component
export interface CalendarHeaderProps
	extends ComponentProps<"div">,
		Pick<GeneralProps, "therapists"> {}

export function CalendarHeader({ className, therapists }: CalendarHeaderProps) {
	const { props: globalProps } = usePage<GlobalPageProps>();
	const { t } = useTranslation("therapist-schedules");

	return (
		<div className={cn("flex sticky top-[64px] z-20 min-h-8", className)}>
			<div className="w-16 md:w-20" /> {/* Empty space for time column */}
			<div className="flex-1">
				<div className="grid grid-flow-col auto-cols-[minmax(200px,1fr)]">
					{therapists.map((therapist) => (
						<HoverCard key={therapist.id} openDelay={10} closeDelay={100}>
							<HoverCardTrigger asChild>
								<Button
									variant="ghost"
									className="border-b line-clamp-1 truncate border-l shadow bg-background uppercase"
								>
									{therapist.name}
								</Button>
							</HoverCardTrigger>
							<HoverCardContent className="flex w-64 flex-col gap-0.5 text-xs">
								<div className="flex gap-4 flex-col">
									<div className="flex items-center gap-2 text-left">
										<Avatar className="w-8 h-8 border rounded-lg bg-muted">
											<AvatarImage src="#" alt={therapist.name} />
											<AvatarFallback
												className={cn(
													"text-xs rounded-lg",
													therapist.user.email ===
														globalProps.auth.currentUser?.user.email
														? "bg-primary text-primary-foreground"
														: therapist.user["isOnline?"]
															? "bg-emerald-700 text-white"
															: "",
												)}
											>
												{generateInitials(therapist.name)}
											</AvatarFallback>
										</Avatar>
										<div className="flex-1 space-y-0.5 leading-tight text-left">
											<p className="font-bold uppercase truncate max-w-52 md:max-w-16 lg:max-w-full">
												{therapist.name}
											</p>
											<p className="font-light">
												#{therapist.registrationNumber}
											</p>
										</div>
									</div>

									<Separator />

									<div className="flex w-full flex-col gap-2">
										<dl className="flex items-center justify-between gap-2">
											<dt className="text-muted-foreground text-nowrap">
												{t("therapist_card.type")}
											</dt>
											<dd className="font-medium text-right">
												{therapist.employmentType}
											</dd>
										</dl>
										<dl className="flex items-center justify-between gap-2">
											<dt className="text-muted-foreground text-nowrap">
												{t("therapist_card.therapist_at")}
											</dt>
											<dd className="font-medium text-right">
												{therapist.service.name.replaceAll("_", " ")}
											</dd>
										</dl>
									</div>
								</div>
							</HoverCardContent>
						</HoverCard>
					))}
				</div>
			</div>
		</div>
	);
}

// * for calendar body
export interface CalendarBodyContainerProps extends ComponentProps<"div"> {}

export function CalendarBodyContainer({
	className,
	children,
}: CalendarBodyContainerProps) {
	return <div className={cn("flex", className)}>{children}</div>;
}

// * for time slot component
interface TimeSlotProps
	extends ComponentProps<"div">,
		Pick<GeneralProps, "selectedDate"> {
	time: string;
	label: string;
	isPastTime: boolean;
	isCurrentTime: boolean;
}

function TimeSlot({
	className,
	time,
	selectedDate,
	label,
	isPastTime,
	isCurrentTime,
}: TimeSlotProps) {
	const { locale, tzDate } = useDateContext();
	const isTodayDate = useMemo(() => {
		const parsedTime = parse(time, "HH:mm", new Date(selectedDate), {
			locale,
			in: tzDate,
		});
		return isToday(parsedTime);
	}, [locale, tzDate, time, selectedDate]);

	return (
		<div
			className={cn(
				"text-xs relative text-right",
				className,
				isPastTime && "bg-sidebar text-muted-foreground",
				isCurrentTime && "bg-primary text-primary-foreground font-bold",
				isPastTime && isCurrentTime && !isTodayDate && "bg-primary/50",
			)}
			style={{
				height: `${SLOT_HEIGHT}px`,
			}}
		>
			<div className="ml-4 border-t md:ml-8">
				<p className="absolute top-[-8px] right-0 font-light uppercase">
					{label}
				</p>
			</div>
		</div>
	);
}

export interface CalendarTimeSlotProps
	extends ComponentProps<"div">,
		Pick<GeneralProps, "selectedDate" | "timeSlots"> {
	currentTimeSlot: string;
}

export function CalendarTimeSlot({
	className,
	selectedDate,
	timeSlots,
	currentTimeSlot,
}: CalendarTimeSlotProps) {
	const { locale, tzDate, timeFormatDateFns, timeFormat } = useDateContext();

	return (
		<div className={cn("flex flex-col w-16 md:w-20 bg-background", className)}>
			{timeSlots?.map((time) => {
				return (
					<TimeSlot
						key={time}
						time={time}
						selectedDate={selectedDate}
						label={formatTimeLabel({
							time,
							dateOpt: {
								locale,
								in: tzDate,
								timeFormat,
								timeFormatDateFns,
							},
						})}
						isPastTime={checkPastTimeSlot({
							time,
							date: selectedDate,
							dateOpt: {
								locale,
								in: tzDate,
							},
						})}
						isCurrentTime={time.includes(currentTimeSlot)}
					/>
				);
			})}
		</div>
	);
}

// * for therapist slot component
interface TherapistSlotProps
	extends ComponentProps<"div">,
		Pick<GeneralProps, "selectedDate"> {
	time: string;
	therapist: Therapist;
}

function TherapistSlot({
	className,
	therapist,
	time,
	selectedDate,
}: TherapistSlotProps) {
	const { locale, tzDate } = useDateContext();
	const appointments = useMemo(() => {
		const list = therapist?.activeAppointments?.filter((a) => {
			if (!a?.startTime) return;

			const startTimeParsed = parse(a?.startTime, "HH:mm", new Date(), {
				locale,
				in: tzDate,
			});
			const timeSlotParsed = parse(time, "HH:mm", new Date(), {
				locale,
				in: tzDate,
			});

			return isSameHour(startTimeParsed, timeSlotParsed);
		});

		return list;
	}, [therapist, time, locale, tzDate]);
	const isPastTime = useMemo(
		() =>
			checkPastTimeSlot({
				time,
				date: selectedDate,
				dateOpt: {
					locale,
					in: tzDate,
				},
			}),
		[locale, tzDate, time, selectedDate],
	);

	return (
		<div
			className={cn(
				"relative border-b border-l border-border",
				className,
				isPastTime && "bg-sidebar",
			)}
			style={{
				height: `${SLOT_HEIGHT}px`,
			}}
		>
			{appointments?.map((appointment) => (
				<AppointmentBlock
					key={appointment.id}
					therapist={therapist}
					appointment={appointment}
					isPastTime={isPastTime}
					isRedirect={true}
				/>
			))}
		</div>
	);
}

export interface CalendarTherapistSlotProps
	extends ComponentProps<"div">,
		Pick<GeneralProps, "selectedDate" | "therapists" | "timeSlots"> {}

export function CalendarTherapistSlot({
	className,
	therapists,
	timeSlots,
	selectedDate,
}: CalendarTherapistSlotProps) {
	const { locale, tzDate } = useDateContext();

	return (
		<div className={cn("flex-1 overflow-x-auto", className)}>
			<div className="grid grid-flow-col auto-cols-[minmax(200px,1fr)] min-w-max">
				{therapists?.map((therapist) => {
					// Determine the availability block for this therapist and selected date.
					const availabilities = getTherapistAvailabilityForDate({
						therapist,
						date: selectedDate,
						dateOpt: { locale, in: tzDate },
					});

					return (
						<div key={therapist.id} className="relative">
							{timeSlots?.map((time) => (
								<TherapistSlot
									key={time}
									time={time}
									therapist={therapist}
									selectedDate={selectedDate}
								/>
							))}

							{!!availabilities?.length &&
								availabilities?.map((availability) => (
									<AvailabilityBlock
										key={`${availability.startTime}-${availability.startTime}`}
										selectedDate={selectedDate}
										startTime={availability.startTime}
										endTime={availability.endTime}
									/>
								))}
						</div>
					);
				})}
			</div>
		</div>
	);
}

// * for the appointment blocks component
export interface AppointmentBlockProps extends ComponentProps<"div"> {
	isPastTime: boolean;
	appointment: Appointment;
	therapist: Therapist;
	slotHeight?: number;
	intervalMinutes?: number;
	isRedirect?: boolean;
}

export const AppointmentBlock: React.FC<AppointmentBlockProps> = ({
	appointment,
	therapist,
	isPastTime,
	slotHeight = SLOT_HEIGHT,
	intervalMinutes = INTERVAL_MINUTES,
	className,
	isRedirect,
}) => {
	const isMobile = useIsMobile();
	const { t } = useTranslation("therapist-schedules");
	const { locale, tzDate, timeFormatDateFns, timeFormat } = useDateContext();
	const appointmentTime = useMemo(() => {
		if (!appointment?.startTime || !appointment?.endTime) return "";

		const startTime = formatTimeLabel({
			time: appointment.startTime,
			type: "appointment-block",
			dateOpt: {
				locale,
				in: tzDate,
				timeFormat,
				timeFormatDateFns,
			},
		});
		const endTime = formatTimeLabel({
			time: appointment.endTime,
			type: "appointment-block",
			dateOpt: {
				locale,
				in: tzDate,
				timeFormat,
				timeFormatDateFns,
			},
		});

		return `${startTime} — ${endTime}`;
	}, [
		appointment.startTime,
		appointment.endTime,
		locale,
		tzDate,
		timeFormatDateFns,
		timeFormat,
	]);
	// calculate the height of the component
	const heightBlock = useMemo(() => {
		if (!appointment?.startTime || !appointment?.endTime) return 0;

		const [startH, startM] = appointment.startTime.split(":").map(Number);
		const [endH, endM] = appointment.endTime.split(":").map(Number);

		const start = startH * 60 + startM;
		const end = endH * 60 + endM;
		const duration = end - start;

		const heightPerMinute = slotHeight / intervalMinutes;

		return duration * heightPerMinute;
	}, [appointment.startTime, appointment.endTime, intervalMinutes, slotHeight]);
	// Calculate the top offset based on the minutes past the hour.
	const topOffset = useMemo(() => {
		if (!appointment?.startTime) return 0;

		const [_, startM] = appointment.startTime.split(":").map(Number);

		return (startM / intervalMinutes) * slotHeight;
	}, [appointment.startTime, intervalMinutes, slotHeight]);
	const blockColor = useMemo(
		() => appointmentBlockClass(appointment.status),
		[appointment.status],
	);
	const patientDetails = useMemo(
		() => appointment.patient,
		[appointment.patient],
	);
	const patientMedicalRecord = useMemo(
		() => appointment.patientMedicalRecord,
		[appointment.patientMedicalRecord],
	);
	const therapistDetails = useMemo(() => therapist, [therapist]);
	const picList = useMemo(() => appointment.admins, [appointment.admins]);
	const genderTitle = useMemo(() => {
		return appointment.patient?.gender === "MALE"
			? "Bapak "
			: appointment.patient?.gender === "FEMALE"
				? "Ibu "
				: "";
	}, [appointment.patient?.gender]);

	if (isRedirect) {
		return (
			<button
				type="button"
				className={cn(
					"absolute left-1 right-1 z-20 p-2 rounded shadow cursor-pointer inset-2 border border-border h-full font-normal",
					blockColor,
					className,
					isPastTime && "opacity-75",
				)}
				style={{
					top: `${topOffset}px`, // set the offset from the top
					height: `${heightBlock}px`,
				}}
				onClick={() => {
					window.open(
						`/admin-portal/appointments?registration_number=${appointment.registrationNumber}`,
						"_blank",
					);
				}}
			>
				<div className="flex flex-col items-start justify-between w-full h-full text-xs line-clamp-1">
					<div className="w-full space-y-1 text-left">
						<div className="flex justify-between gap-3">
							<div>
								<span>Visit {appointment.visitProgress}</span>
								{appointment.package && (
									<>
										<span className="mx-1">&bull;</span>
										<span>{appointment.package.name}</span>
									</>
								)}
							</div>
							<Badge variant="outline" className="bg-card px-1">
								<span className="uppercase tracking-tight text-[0.7rem]">
									{appointment.status}
								</span>
							</Badge>
						</div>

						<p className="text-sm font-semibold tracking-tight leading-tight capitalize">
							{`${genderTitle} ${appointment.patient?.name || "(No patient)"}`}
						</p>
					</div>

					<div className="flex w-full items-center justify-between gap-3">
						<span className="font-light uppercase">{appointmentTime}</span>
						<span className="font-light italic">
							#{appointment.registrationNumber}
						</span>
					</div>
				</div>
			</button>
		);
	}

	return (
		<Sheet>
			<SheetTrigger asChild>
				<Button
					variant="link"
					className={cn(
						"absolute left-1 right-1 z-20 p-2 rounded shadow cursor-pointer inset-2 border border-border h-full",
						blockColor,
						className,
						isPastTime && "opacity-75",
					)}
					style={{
						top: `${topOffset}px`, // set the offset from the top
						height: `${heightBlock}px`,
					}}
					onClick={() => {}}
				>
					<div className="flex flex-col items-start justify-between w-full h-full text-xs line-clamp-1">
						<div className="w-full space-y-0.5 text-left">
							<div className="flex gap-1">
								<p className="flex items-center gap-0.5 uppercase font-bold text-[10px]">
									<Hash className="!size-2.5" />
									<span>{appointment.registrationNumber}</span>
								</p>

								<span>&bull;</span>

								<p className="font-light">Visit {appointment.visitProgress}</p>
							</div>

							<p className="font-semibold tracking-wide uppercase">
								{patientDetails?.name}
							</p>
						</div>

						<p className="font-light uppercase">{appointmentTime}</p>
					</div>
				</Button>
			</SheetTrigger>

			<SheetContent
				side={isMobile ? "bottom" : "right"}
				className="max-h-screen p-0 overflow-auto bg-sidebar"
			>
				<div className="flex flex-col w-full h-full px-6">
					<SheetHeader className="flex-none py-6 bg-sidebar">
						<SheetTitle className="flex items-center gap-2">
							<p className="flex items-center gap-0.5 uppercase font-bold">
								<Hash className="!size-4" />
								{appointment.registrationNumber}
							</p>

							<span>&bull;</span>

							<p className="font-light">Visit {appointment.visitProgress}</p>
						</SheetTitle>
					</SheetHeader>

					<Suspense
						fallback={
							<div className="w-full p-3 border rounded-lg border-border bg-background text-muted-foreground">
								<LoadingBasic columnBased={true} />
							</div>
						}
					>
						<div className="grid flex-1 gap-8 py-4 overflow-y-auto text-sm">
							<AppointmentDetailsSection appointment={appointment} />
							{patientDetails && (
								<PatientDetailsSection
									patientDetails={patientDetails}
									patientMedicalRecord={patientMedicalRecord}
								/>
							)}

							{therapistDetails && (
								<TherapistDetailsSection therapistDetails={therapistDetails} />
							)}

							{!!picList?.length && <PICDetailsSection picList={picList} />}
						</div>
					</Suspense>

					<SheetFooter className="sticky bottom-0 left-0 flex-none py-6 bg-sidebar">
						<SheetClose asChild>
							<Button variant="primary-outline">{t("close")}</Button>
						</SheetClose>
					</SheetFooter>
				</div>
			</SheetContent>
		</Sheet>
	);
};

// Renders a translucent block representing the therapist's available time slot.
interface AvailabilityBlockProps
	extends ComponentProps<"div">,
		Pick<GeneralProps, "selectedDate"> {
	startTime: string;
	endTime: string;
	slotHeight?: number;
	intervalMinutes?: number;
}
const AvailabilityBlock: React.FC<AvailabilityBlockProps> = ({
	className,
	selectedDate,
	startTime,
	endTime,
	slotHeight = SLOT_HEIGHT,
	intervalMinutes = INTERVAL_MINUTES,
}) => {
	const [ref, hovering] = useHover();
	const { t } = useTranslation("therapist-schedules");
	const { locale, tzDate, timeFormatDateFns, timeFormat } = useDateContext();
	const heightBlock = useMemo(() => {
		const [startH, startM] = startTime.split(":").map(Number);
		const [endH, endM] = endTime.split(":").map(Number);

		const startTotal = startH * 60 + startM;
		const endTotal = endH * 60 + endM;

		// Clip the start if it's before the visible calendar start.
		const visibleDuration = endTotal - Math.max(startTotal, START_HOUR * 60);
		return (visibleDuration / intervalMinutes) * slotHeight;
	}, [startTime, endTime, intervalMinutes, slotHeight]);
	const topOffset = useMemo(() => {
		const [startH, startM] = startTime.split(":").map(Number);
		const startTotal = startH * 60 + startM;
		// Calculate the visible start (in minutes) from the configured calendar start
		const displayStartTotal = START_HOUR * 60;
		// Adjust so that if startTime equals the calendar START_HOUR, topOffset becomes 0
		return ((startTotal - displayStartTotal) / intervalMinutes) * slotHeight;
	}, [startTime, intervalMinutes, slotHeight]);
	const availableTime = useMemo(() => {
		const start = formatTimeLabel({
			time: startTime,
			type: "appointment-block",
			dateOpt: {
				locale,
				in: tzDate,
				timeFormat,
				timeFormatDateFns,
			},
		});
		const end = formatTimeLabel({
			time: endTime,
			type: "appointment-block",
			dateOpt: {
				locale,
				in: tzDate,
				timeFormat,
				timeFormatDateFns,
			},
		});

		return `${start} — ${end}`;
	}, [startTime, endTime, locale, tzDate, timeFormatDateFns, timeFormat]);
	const isPastTime = useMemo(
		() =>
			checkPastTimeSlot({
				time: startTime,
				date: selectedDate,
				dateOpt: {
					locale,
					in: tzDate,
				},
			}),
		[locale, tzDate, startTime, selectedDate],
	);
	const isUnder2Hours = useMemo(
		() =>
			checkUnder2Hours({
				startTimeStr: startTime,
				endTimeStr: endTime,
				dateOpt: { locale, in: tzDate },
			}),
		[startTime, endTime, locale, tzDate],
	);

	return (
		<div
			ref={ref}
			className={cn(
				"absolute flex flex-col items-start justify-between p-2 text-xs rounded shadow left-1 right-1 inset-1 bg-purple-800/25 text-purple-800 dark:bg-purple-300/25 dark:text-purple-300 border border-border transition-all",
				className,
				isPastTime && "opacity-75",
				hovering && "-translate-y-4 z-10",
			)}
			style={{
				top: `${topOffset}px`,
				height: `${heightBlock}px`,
			}}
		>
			{!isUnder2Hours ? (
				<>
					<p className="flex items-center font-medium uppercase truncate">
						{t("title")}
					</p>

					<p className="font-light uppercase">{availableTime}</p>
				</>
			) : (
				<p className="flex items-center font-medium truncate">
					{t("calendar.block.availability.title")} (
					{availableTime.toUpperCase()})
				</p>
			)}
		</div>
	);
};
